import {
  ArrowUpRight,
  ChartPie,
  GlobeHemisphereWest,
  LinkedinLogo,
  Link as LinkIcon,
  SealCheck,
  SlidersHorizontal,
  Warning,
} from "@phosphor-icons/react";
import clsx from "clsx";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "common/components/ui/Accordion";
import { Button } from "common/components/ui/Button";
import { Dialog, OverlayDialogContent } from "common/components/ui/Dialog";
import ProfileImage from "common/components/ui/ProfileImage";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { Sidebar } from "common/layouts/WorkspaceLayout";

import useAccountActions from "../../hooks/useAccountActions";
import AccountActionsDropdown from "../AccountActionsDropdown";
import AccountName from "../AccountName";
import LicenseTag from "../AccountTags/LicenseTag";
import LoginStatusTag from "../AccountTags/LoginStatusTag";
import AccountInteractions from "./AccountInteractions";
import AccountProxy from "./AccountProxy";
import AccountSSI from "./AccountSSI";
import RentedBanner from "./RentedBanner";
import SettingsBanner from "./SettingsBanner";

import type { JSX, ReactNode } from "react";

function DetailItem({
  label,
  icon,
  children,
  isColumn = false,
}: {
  label: string;
  icon: JSX.Element;
  children: ReactNode;
  isColumn?: boolean;
}) {
  return (
    <div
      className={clsx(
        "flex px-4 justify-between border-b border-b-black-200",
        isColumn ? "flex-col pt-2.5 pb-4 gap-y-2.5" : "py-2 items-center",
      )}
    >
      <div className="gap-x-1.5 flex text-button-sm [&>svg]:size-5">
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}

export default function AccountSettings() {
  const isDesktop = useTwBreakpoint("lg");
  const { accountId } = useParams();
  const { accounts } = useSelectedWorkspaceContext();
  const navigate = useNavigate();
  const location = useLocation();

  const account = accounts.find(({ id }) => id === accountId);
  const { occupation, state, license, picture_url, type, public_identifier } =
    account ?? {};

  const { selectAction, components } = useAccountActions(account);

  if (!account) {
    return <Navigate to={{ pathname: ".." }} replace />;
  }

  function goBack() {
    navigate({
      pathname: "..",
      search: location.search, // Preserve query params
    });
  }

  const accordionItems = [
    {
      icon: SlidersHorizontal,
      label: "Interaction limits / week",
      component: (
        <AccountInteractions
          accountId={accountId}
          isRented={type === "rental"}
        />
      ),
    },
    ...(type === "user"
      ? [
          {
            icon: ChartPie,
            label: "SSI score",
            component:
              state === "LoggedOut" ? (
                <SettingsBanner
                  variant="warning"
                  text="Cannot show SSI for disconnected accounts"
                  actionComponent={
                    <Button
                      size="sm"
                      onClick={() => {
                        selectAction("reconnect");
                      }}
                    >
                      Reconnect
                    </Button>
                  }
                />
              ) : (
                <AccountSSI account={account} />
              ),
          },
          {
            icon: GlobeHemisphereWest,
            label: "Proxy",
            component: <AccountProxy account={account} />,
          },
        ]
      : [
          {
            icon: Warning,
            label: "Danger zone",
            component: <RentedBanner />,
          },
        ]),
  ];

  const content = (
    <>
      {/* Header */}
      <div className="flex max-lg:pl-16 px-4 py-3 lg:py-4 border-b border-b-black-200 gap-x-2 lg:gap-x-2.5 items-start">
        <div className="flex gap-x-3 items-start flex-1">
          <ProfileImage src={picture_url} size={isDesktop ? "lg" : "md"} />

          <div className="flex flex-col gap-y-1">
            <AccountName
              account={account}
              className="text-sm-bold lg:text-lg-bold"
            />
            <p className="text-black-500 text-xs-medium lg:text-sm-medium">
              {occupation}
            </p>
          </div>
        </div>
        <RenderIf condition={!isDesktop}>
          <RenderIf condition={account.type === "user"}>
            <AccountActionsDropdown account={account} />
          </RenderIf>
        </RenderIf>
      </div>

      {/* URL */}
      <DetailItem label="LinkedIn" icon={<LinkedinLogo weight="fill" />}>
        <Button
          rightIcon={<ArrowUpRight />}
          variant="link-purple"
          size="sm"
          asChild
        >
          <Link
            target="_blank"
            to={`https://linkedin.com/in/${public_identifier}`}
            rel="noreferrer"
          >
            View profile
          </Link>
        </Button>
      </DetailItem>
      {/* Login Status */}
      <DetailItem label="Login" icon={<LinkIcon weight="bold" />}>
        <LoginStatusTag account={account} size="md" />
      </DetailItem>
      {/* Seat */}
      <DetailItem label="Seat" icon={<SealCheck weight="fill" />}>
        <LicenseTag isRented={type === "rental"} license={license} size="md" />
      </DetailItem>
      {/* Settings */}
      <Accordion type="single" collapsible>
        {accordionItems.map(({ icon: Icon, label, component }) => (
          <AccordionItem key={label} value={label} className="last:border-b">
            <AccordionTrigger className="px-4 py-2.5">
              <Icon size={20} weight="fill" />
              {label}
            </AccordionTrigger>
            <AccordionContent className="py-3 flex flex-col gap-y-3">
              {component}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      {components}
    </>
  );

  if (isDesktop) {
    return (
      <Sidebar
        isOpen={true}
        onClose={() => navigate({ pathname: "..", search: location.search })}
      >
        {content}
      </Sidebar>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          goBack();
        }
      }}
    >
      <OverlayDialogContent>{content}</OverlayDialogContent>
    </Dialog>
  );
}
